import Skeleton from './Skeleton';
import { StyledPageSkeleton } from './PageSkeletons.styles';

/**
 * Shared page-level loading placeholders.
 *
 * Building blocks for skeletons that are not worth a page-specific one. Whole
 * pages keep their own skeleton next to the component, shown by PageReveal
 * until the page's data is in.
 *
 * Each variant mirrors the real layout closely enough that nothing jumps when
 * the data lands.
 */

/** Four stat tiles, the shape most overview pages open with. */
export const StatsSkeleton = ({ count = 4 }) => (
  <div className="sk-stats">
    {Array.from({ length: count }).map((_, i) => (
      <div className="sk-stat" key={`sk-stat-${i}`}>
        <Skeleton width={96} height={12} radius="6px" resp />
        <Skeleton width={64} height={26} radius="8px" margin="12px 0 0" resp />
        <Skeleton width={110} height={11} radius="6px" margin="12px 0 0" resp />
      </div>
    ))}
  </div>
);

/** Stacked rows — approvals, invites, message lists. */
export const RowListSkeleton = ({ count = 5, avatar = true }) => (
  <div className="sk-list">
    {Array.from({ length: count }).map((_, i) => (
      <div className="sk-row" key={`sk-row-${i}`}>
        {avatar && <Skeleton width={40} height={40} circle resp />}
        <div className="sk-row-lines">
          <Skeleton width="35%" height={14} radius="6px" resp />
          <Skeleton width="65%" height={12} radius="6px" margin="8px 0 0" resp />
        </div>
        <Skeleton width={88} height={28} radius="10px" resp />
      </div>
    ))}
  </div>
);

/** Header strip plus rows, for table-shaped pages. */
export const TableSkeleton = ({ rows = 8, columns = 5 }) => (
  <div className="sk-table">
    <div className="sk-thead">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={`sk-th-${i}`} width="60%" height={11} radius="6px" resp />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, r) => (
      <div className="sk-trow" key={`sk-tr-${r}`}>
        {Array.from({ length: columns }).map((_, c) => (
          <Skeleton key={`sk-td-${r}-${c}`} width={c === 0 ? '80%' : '55%'} height={14} radius="6px" resp />
        ))}
      </div>
    ))}
  </div>
);

/** Grouped label-and-field cards, for form-shaped pages. */
export const FormSkeleton = ({ groups = 3, fields = 3 }) => (
  <>
    {Array.from({ length: groups }).map((_, g) => (
      <div className="sk-section" key={`sk-form-${g}`}>
        {Array.from({ length: fields }).map((_, f) => (
          <div key={`sk-field-${g}-${f}`} style={{ marginTop: f === 0 ? 0 : 20 }}>
            <Skeleton width={110} height={12} radius="6px" resp />
            <Skeleton width="100%" height={40} radius="10px" margin="8px 0 0" resp />
          </div>
        ))}
      </div>
    ))}
  </>
);

/**
 * Wrapper that supplies the layout the variants rely on.
 *
 * `padded` adds the page gutter for skeletons that stand in for a whole route;
 * leave it off when the host already provides one.
 */
const PageSkeleton = ({ children, padded = true, maxWidth }) => (
  <StyledPageSkeleton $padded={padded} $maxWidth={maxWidth}>
    <div className="sk-page">{children}</div>
  </StyledPageSkeleton>
);

export default PageSkeleton;
